import { Fragment } from "react";
import { isOfferItem, type Resource, type ShopItem, type SlotFreeSpinsOfferLevel, type VipStatus } from "../../types";
import { formatNumber, getCityName, getCityVoucherIcon, getOldPrice, isKnownCity, pluralize } from "../../utils";
import CountdownTimer from "../CountdownTimer";
import styles from "./styles.module.css";

export type CardVariant = "redOffer" | "violetOffer" | "purple" | "orange" | "common";

type Props = {
  product: ShopItem;
  onChooseClick: () => void;
  variant?: CardVariant;
  expirationDate?: number;
  vipStatus?: VipStatus | null;
  hideLabel?: boolean;
};

const FREE_SPINS_FORMS = { one: "фриспин", few: "фриспина", many: "фриспинов" };

const FREE_SPINS_LEVEL_TEXT: Record<SlotFreeSpinsOfferLevel, string> = {
  Low: "больше спинов",
  Middle: "оптимально",
  High: "крупная ставка",
};

const RESOURCE_ICONS: Record<string, string> = {
  Chips: "/chips_icon_pill.svg",
  Tickets: "/tickets_icon_pill.svg",
};

function getLabelText(variant: CardVariant, labelType: string | null): string {
  if (variant === "redOffer" || variant === "violetOffer") return "успей купить";
  if (labelType === "bestPrice") return "лучшая цена";
  return "хит продаж";
}

export default function ShopItemView({
  product,
  onChooseClick,
  variant = "common",
  expirationDate,
  vipStatus,
  hideLabel = false,
}: Props) {
  const isOffer = variant === "redOffer" || variant === "violetOffer";
  const hasLabel = variant !== "common" && !hideLabel;
  const bundle = product.rewardBundle;
  const vipBundle = product.vipRewardBundle;
  const freeSpins = bundle.slotFreeSpins;
  const freeSpinsOffer = isOfferItem(product) ? product.slotFreeSpinsOffer : null;
  const labelText = getLabelText(variant, product.labelType);

  const isVipActive = !freeSpins && vipStatus?.isActive && vipBundle.resources.length > 0;
  const vouchers = (isVipActive && vipBundle.cityVouchers.length > 0 ? vipBundle : bundle).cityVouchers.filter((v) =>
    isKnownCity(v.cityId),
  );

  function getVipAmount(resource: Resource, index: number): number | undefined {
    if (!isVipActive) return undefined;
    return (vipBundle.resources.find((r) => r.currency === resource.currency) ?? vipBundle.resources[index])?.amount;
  }

  function renderResourceIcon(currency: string) {
    const icon = RESOURCE_ICONS[currency];
    return icon && <img className={styles.resourceIcon} src={icon} alt="" width="16" height="16" />;
  }

  return (
    <div className={styles.wrapper} onClick={onChooseClick}>
      {hasLabel && (
        <div className={`${styles.labelTab} ${styles[variant]}`}>
          <span>{labelText}</span>
        </div>
      )}
      <div className={`${styles.card} ${styles[variant]} ${hasLabel ? styles.hasLabel : ""}`}>
        {isVipActive && product.purchaseBonusPercent && product.purchaseBonusPercent > 0 && (
          <div className={styles.vipBonusBadge}>
            <img src="/vip_icon_pill.svg" alt="VIP" width="16" height="16" />
            <span>+{product.purchaseBonusPercent}%</span>
          </div>
        )}
        <div className={styles.imageWrap}>
          <img src={product.image} alt={product.title} />
        </div>
        <div className={styles.middle}>
          <div className={styles.amountContainer}>
            {freeSpins ? (
              <>
                {freeSpinsOffer && (
                  <div className={`${styles.levelChip} ${styles[freeSpinsOffer.level]}`}>
                    {FREE_SPINS_LEVEL_TEXT[freeSpinsOffer.level]}
                  </div>
                )}
                <div className={`${styles.amount} ${styles[variant]}`}>
                  {formatNumber(freeSpins.count)} {pluralize(freeSpins.count, FREE_SPINS_FORMS)}
                </div>
                <div className={styles.subAmount}>ставка {formatNumber(freeSpins.bet)}</div>
                {freeSpinsOffer && freeSpinsOffer.availableWin > 0 && (
                  <div className={styles.winAmount}>выигрыш до {formatNumber(freeSpinsOffer.availableWin)}</div>
                )}
              </>
            ) : (
              <div className={styles.rewards}>
                {bundle.resources.length > 0 && (
                  <div className={styles.amountContainer}>
                    {bundle.resources.map((resource, index) => {
                      const vipAmount = getVipAmount(resource, index);
                      return vipAmount != null ? (
                        <Fragment key={resource.currency}>
                          <div className={`${styles.amount} ${styles[variant]} ${styles.strikethrough}`}>
                            {renderResourceIcon(resource.currency)}
                            {formatNumber(resource.amount)}
                          </div>
                          <div className={`${styles.amount} ${styles[variant]} ${styles.vipAmount}`}>
                            {renderResourceIcon(resource.currency)}
                            {formatNumber(vipAmount)}
                          </div>
                        </Fragment>
                      ) : (
                        <div key={resource.currency} className={`${styles.amount} ${styles[variant]}`}>
                          {renderResourceIcon(resource.currency)}
                          {formatNumber(resource.amount)}
                        </div>
                      );
                    })}
                  </div>
                )}
                {vouchers.length > 0 && (
                  <div className={styles.vouchers}>
                    {vouchers.map((voucher) => (
                      <div key={voucher.cityId} className={styles.voucher} title={`Ваучер: ${getCityName(voucher.cityId)}`}>
                        <img src={getCityVoucherIcon(voucher.cityId)} alt="" />
                        <div className={styles.voucherText}>
                          <span className={`${styles.amount} ${styles[variant]} ${styles.voucherCount}`}>
                            ×{formatNumber(voucher.amount)}
                          </span>
                          <span className={styles.voucherCity}>{getCityName(voucher.cityId)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          {isOffer && expirationDate != null && (
            <div className={styles.timer}>
              <CountdownTimer targetEpochSeconds={expirationDate} />
            </div>
          )}
        </div>
        <button
          className={`${styles.buyButton} ${styles[variant]}`}
          onClick={(e) => {
            e.stopPropagation();
            onChooseClick();
          }}
        >
          {product.discount > 0 && (
            <div className={`${styles.discountBadge} ${styles[variant]}`}>
              <span>-{product.discount}%</span>
            </div>
          )}
          {product.discount > 0 && (
            <div className={styles.oldPrice}>
              {formatNumber(getOldPrice(product.price, product.discount))} ₽
            </div>
          )}
          <div className={styles.currentPrice}>{formatNumber(product.price)} ₽</div>
        </button>
      </div>
    </div>
  );
}
